import { Except } from '@common/types/common.type';
import { clsxm } from '@ui/utils/style.util';
import { ComponentProps, ReactNode, memo, useMemo } from 'react';

// #region Types
export type TabItemProps = Except<ComponentProps<'button'>, 'title'> & {
  active?: boolean;
  containerClassName?: string;
  title: ReactNode;
};

export type TabProps = ComponentProps<'menu'> & { itemClassName?: string; items: TabItemProps[] };
// #endregion

// #region Styles
const ITEM_STYLES = {
  false: 'text-green-dark',
  true: 'text-black-charcoal',
};

const ITEM_BORDER_STYLES = {
  false: 'bg-transparent',
  true: 'bg-black-charcoal',
};
// #endregion

function BaseTab({ className, itemClassName, items, ...rest }: TabProps) {
  // #region Content
  const tabItems = useMemo(
    () =>
      items.map(
        (
          {
            active = false,
            children,
            className: itemInnerClassName,
            containerClassName,
            key,
            title,
            ...itemRest
          },
          index,
        ) => {
          return (
            <li key={key ?? index} className={containerClassName}>
              <button
                type="button"
                {...itemRest}
                className={clsxm(
                  'relative h-full font-semibold leading-6 py-2 px-1 duration-100 cursor-pointer',
                  ITEM_STYLES[`${active}`],
                  itemClassName,
                  itemInnerClassName,
                )}
              >
                {children ?? title}

                <div
                  className={clsxm(
                    'absolute left-0 right-0 bottom-0 h-[3px] duration-200',
                    ITEM_BORDER_STYLES[`${active}`],
                  )}
                />
              </button>
            </li>
          );
        },
      ),
    [items, itemClassName],
  );
  // #endregion

  return (
    <menu {...rest} className={clsxm('flex justify-evenly space-x-1', className)}>
      {tabItems}
    </menu>
  );
}
BaseTab.displayName = 'Tab';

const Tab = memo(BaseTab);

export default Tab;
